'use client';

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import DangerousIcon from '@mui/icons-material/Dangerous';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { DataGrid, Badge, Spinner } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { Device, DevicesApiResponse } from '@repo/shared/domain';
import { DeviceForm } from '@repo/forms';
import { sortToString, stringToSort, constructQuery, guardedClient as clientRequest } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

interface DevicesSearch {
	sort?: string;
	page?: number;
}

const getDevices = async (query: string): Promise<DevicesApiResponse> => {
	const { body } = await clientRequest.get<undefined, DevicesApiResponse>(`/api/devices?${query}`, {
		schema: undefined,
	});
	return body;
};

export const Route = createFileRoute('/_admin/config/devices/')({
	validateSearch: (search: Record<string, unknown>): DevicesSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
	}),
	beforeLoad: ({ search }) => {
		if (!search.sort || search.page === undefined) {
			throw redirect({
				to: '/config/devices',
				search: { sort: 'name:asc', page: 0 },
			});
		}
	},
	component: DevicesPage,
});

const variants = {
	online: 'success',
	offline: 'default',
} as const;

const columns = [
	{
		field: 'hasError',
		headerName: ' ',
		renderCell: (c: { row: Device }) => {
			if (c.row.hasError) {
				return <DangerousIcon fontSize="small" sx={{ color: 'white' }} />;
			}
			return '';
		},
		width: 1,
	},
	{
		field: 'name',
		headerName: 'Name',
		flex: 2,
	},
	{
		field: 'description',
		headerName: 'Description',
		flex: 2,
	},
	{
		field: 'zone.name',
		headerName: 'Zone',
		flex: 2,
		sortable: false,
	},
	{
		field: 'config.direction',
		headerName: 'Direction',
		flex: 1,
		valueFormatter: (params: { value: string }) => String(params.value || '').toUpperCase(),
	},
	{
		field: 'type',
		headerName: 'Device Type',
		flex: 1,
		renderCell: (c: { value: string }) => {
			const label = `${String(c.value || 'Not Configured').toUpperCase()}`;
			return label;
		},
	},
	{
		field: 'currentVersion',
		headerName: 'Version',
		width: 100,
	},
	{
		field: 'inMaintenance',
		headerName: 'Status',
		width: 120,
		sortable: false,
		renderCell: (c: { row: Device }) => {
			const { lastSeenAt, ioStatus, inMaintenance } = c.row;
			const aclStatus = lastSeenAt ? differenceInMinutes(new Date(), parseISO(lastSeenAt)) <= 5 : false;

			if (inMaintenance) {
				return <Badge variant="warning">Maintenance</Badge>;
			}

			return (
				<>
					<Badge variant={variants[aclStatus ? 'online' : 'offline']}>ACL</Badge>
					<Badge variant={variants[ioStatus ? 'online' : 'offline']}>IO</Badge>
				</>
			);
		},
	},
];

function DevicesPage() {
	const searchParams = Route.useSearch();
	const { sort, page } = searchParams;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const [isFormOpen, setIsFormOpen] = useState(false);
	const isSystemAdmin = authenticatedUser?.email?.includes('@nineid.com') || false;

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Devices');

	const query = constructQuery({ sort: sort || 'name:asc', page: page || 0 });

	const { data: devicesData, isLoading } = useQuery({
		queryKey: ['devices', query],
		queryFn: () => getDevices(query),
	});

	const data = devicesData || { data: [], count: 0, page: 0, pageSize: 25 };

	const handleRowClick = ({ row }: { row: Device }) => {
		if (row.type !== 'kiosk' && !isSystemAdmin) {
			return;
		}
		navigate({ to: '/config/devices/$deviceId', params: { deviceId: row.id } });
	};

	const handleOpenForm = () => {
		setIsFormOpen(true);
	};

	const handleCloseForm = () => {
		setIsFormOpen(false);
		revalidateDevices();
	};

	const handleSort = ([sortItem]: { field: string; sort: 'asc' | 'desc' }[]) => {
		if (!sortItem) return;
		updateQueryParams({ sort: sortToString(sortItem) });
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const revalidateDevices = () => {
		queryClient.invalidateQueries({ queryKey: ['devices'] });
	};

	const actions = useMemo(() => {
		const actionButtons = [
			<Button onClick={handleOpenForm} startIcon={<AddIcon />} variant={'outlined'} key="add">
				Add Device
			</Button>,
		];

		if (isSystemAdmin) {
			actionButtons.push(
				<Button onClick={() => navigate({ to: '/config/devices/topology' })} key="devices">
					<DeviceHubIcon />
				</Button>,
			);
		}

		return actionButtons;
	}, [isSystemAdmin, navigate]);

	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<GenericDataPageHeader actions={actions} />
			<div className="p-4 pt-0">
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						data={data.data}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						pagination
						onRowClick={handleRowClick}
						onReload={revalidateDevices}
						pageSize={data.pageSize}
						pageSizeOptions={[data.pageSize]}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={[stringToSort(sort || 'name:asc')]}
						page={data.page}
						rowCount={data.count}
						onSortModelChange={handleSort}
						getRowClassName={(params: { row: Device }) => {
							if (params.row.inMaintenance) {
								return 'warning-row';
							}
							if (params.row.hasError) {
								return 'error-row';
							}
							return '';
						}}
					/>
				</Paper>
			</div>
			<DeviceForm isSystemAdmin={isSystemAdmin} isOpen={isFormOpen} onClose={handleCloseForm} />
		</>
	);
}
